/**
 * Typography Component System
 *
 * Provides consistent, accessible text styles across the application.
 * All text components ensure WCAG AA contrast ratios (4.5:1 for normal text, 3:1 for large text).
 *
 * Usage:
 *   <Heading level={1}>Page Title</Heading>
 *   <Text variant="body">Regular paragraph text</Text>
 *   <Text variant="secondary">Muted secondary text</Text>
 */

import React from 'react';

/**
 * Heading Component
 * @param {1|2|3|4|5|6} level - Heading level (h1-h6)
 * @param {string} className - Additional Tailwind classes
 */
export const Heading = ({ level = 1, children, className = '', ...props }) => {
  const baseClasses = "font-semibold text-foreground";

  const sizeClasses = {
    1: "text-4xl md:text-5xl lg:text-6xl", // 36-60px
    2: "text-3xl md:text-4xl lg:text-5xl", // 30-48px
    3: "text-2xl md:text-3xl lg:text-4xl", // 24-36px
    4: "text-xl md:text-2xl",              // 20-24px
    5: "text-lg md:text-xl",               // 18-20px
    6: "text-base md:text-lg",             // 16-18px
  };

  const Tag = `h${level}`;
  const classes = `${baseClasses} ${sizeClasses[level]} ${className}`;

  return <Tag className={classes} {...props}>{children}</Tag>;
};

/**
 * Text Component
 * @param {string} variant - Text variant (body, secondary, tertiary, small, caption)
 * @param {string} weight - Font weight (normal, medium, semibold, bold)
 * @param {string} as - HTML tag to render (p, span, div)
 */
export const Text = ({
  variant = 'body',
  weight = 'normal',
  as: Component = 'p',
  className = '',
  children,
  ...props
}) => {
  const variantClasses = {
    // Primary body text - highest contrast
    body: "text-foreground text-base leading-relaxed",

    // Secondary text - slightly muted but still WCAG AA compliant
    secondary: "text-default-700 dark:text-default-300 text-base",

    // Tertiary text - more muted but WCAG AA compliant
    tertiary: "text-default-700 dark:text-default-200 text-sm",

    // Small text - WCAG AA compliant
    small: "text-sm text-default-700 dark:text-default-300",

    // Caption/helper text - WCAG AA compliant (larger size helps)
    caption: "text-xs text-default-800 dark:text-default-200",

    // Emphasis text
    emphasis: "text-foreground text-base font-medium",
  };

  const weightClasses = {
    normal: "font-normal",
    medium: "font-medium",
    semibold: "font-semibold",
    bold: "font-bold",
  };

  const classes = `${variantClasses[variant]} ${weightClasses[weight]} ${className}`;

  return <Component className={classes} {...props}>{children}</Component>;
};

/**
 * Label Component - For form labels and metadata
 */
export const Label = ({ required = false, className = '', children, ...props }) => {
  return (
    <label
      className={`text-sm font-medium text-foreground ${className}`}
      {...props}
    >
      {children}
      {required && <span className="text-danger ml-1">*</span>}
    </label>
  );
};

/**
 * Link Component - Accessible links with proper contrast
 */
export const Link = ({
  href,
  external = false,
  className = '',
  children,
  ...props
}) => {
  const baseClasses = "text-blue-700 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200 underline transition-colors";
  const externalProps = external ? {
    target: "_blank",
    rel: "noopener noreferrer"
  } : {};

  return (
    <a
      href={href}
      className={`${baseClasses} ${className}`}
      {...externalProps}
      {...props}
    >
      {children}
      {external && <span className="sr-only"> (opens in new tab)</span>}
    </a>
  );
};

/**
 * Code Component - For inline code snippets
 */
export const Code = ({ className = '', children, ...props }) => {
  return (
    <code
      className={`px-1.5 py-0.5 rounded bg-default-100 dark:bg-default-50 text-sm font-mono text-foreground ${className}`}
      {...props}
    >
      {children}
    </code>
  );
};

/**
 * Blockquote Component
 */
export const Blockquote = ({ className = '', children, ...props }) => {
  return (
    <blockquote
      className={`pl-4 border-l-4 border-primary text-default-700 dark:text-default-300 italic ${className}`}
      {...props}
    >
      {children}
    </blockquote>
  );
};

/**
 * Pre-configured text variants as standalone components
 */
export const Title = ({ children, ...props }) => (
  <Heading level={1} {...props}>{children}</Heading>
);

export const Subtitle = ({ children, ...props }) => (
  <Heading level={2} {...props}>{children}</Heading>
);

export const SectionTitle = ({ children, ...props }) => (
  <Heading level={3} {...props}>{children}</Heading>
);

export const Body = ({ children, ...props }) => (
  <Text variant="body" {...props}>{children}</Text>
);

export const SecondaryText = ({ children, ...props }) => (
  <Text variant="secondary" {...props}>{children}</Text>
);

export const Caption = ({ children, ...props }) => (
  <Text variant="caption" {...props}>{children}</Text>
);

/**
 * Helper hook for dynamic text color based on background
 * Ensures WCAG contrast compliance
 */
export const useTextColor = (backgroundColor) => {
  // This would contain logic to calculate appropriate text color
  // based on background color to ensure WCAG AA compliance
  // For now, relying on HeroUI's foreground colors
  return 'text-foreground';
};

const Typography = {
  Heading,
  Text,
  Label,
  Link,
  Code,
  Blockquote,
  Title,
  Subtitle,
  SectionTitle,
  Body,
  SecondaryText,
  Caption,
};

export default Typography;
